package com.flowyride.feature.planner.ui.components

import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.Orientation
import androidx.compose.foundation.gestures.draggable
import androidx.compose.foundation.gestures.rememberDraggableState
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.unit.Velocity
import kotlinx.coroutines.Job
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.TimeMark
import kotlin.time.TimeSource
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.collapse
import com.flowyride.feature.planner.resources.expand
import org.jetbrains.compose.resources.stringResource
import kotlin.math.abs

/** Resting heights of the [RideSheet]. */
enum class SheetDetent { COLLAPSED, HALF, FULL }

/** Share of the available height the sheet takes half open. */
private const val HALF_RATIO = 0.5f

/** A fling faster than this (px/s) goes to the next detent, whatever the distance dragged. */
private const val FLING_VELOCITY = 800f

/** Wheel notches closer than this make one step. */
private val WHEEL_BURST = 400.milliseconds

/** Pause after which a mouse wheel moving the sheet is considered done. */
private const val WHEEL_SETTLE_MILLIS = 150L

/**
 * Non-modal bottom sheet: the map above it keeps every gesture. [header] always shows (collapsed, it is all
 * that shows); [content] scrolls below it once the sheet is pulled up. Dragging the header moves the sheet,
 * a tap on it opens or folds it; scrolling the content past its top or bottom does too.
 *
 * @param maxHeight room the sheet may take fully open (below the app's top bar).
 */
@Composable
internal fun RideSheet(
    detent: SheetDetent,
    onDetentChange: (SheetDetent) -> Unit,
    maxHeight: Dp,
    modifier: Modifier = Modifier,
    header: @Composable ColumnScope.() -> Unit,
    content: @Composable ColumnScope.() -> Unit,
) {
    val density = LocalDensity.current
    val navigationBar = WindowInsets.navigationBars.getBottom(density)
    var headerHeight by remember { mutableIntStateOf(0) }
    val maxPx = with(density) { maxHeight.toPx() }
    val collapsedPx = (headerHeight + navigationBar).toFloat().coerceAtMost(maxPx)
    val halfPx = (maxPx * HALF_RATIO).coerceAtLeast(collapsedPx)
    fun heightOf(target: SheetDetent) = when (target) {
        SheetDetent.COLLAPSED -> collapsedPx
        SheetDetent.HALF -> halfPx
        SheetDetent.FULL -> maxPx
    }

    val height = remember { Animatable(0f) }
    var dragged by remember { mutableStateOf<Float?>(null) }
    val currentDetent by rememberUpdatedState(detent)
    val currentOnDetentChange by rememberUpdatedState(onDetentChange)
    val measured = headerHeight > 0
    LaunchedEffect(detent, collapsedPx, halfPx, maxPx, measured) {
        if (!measured || dragged != null) return@LaunchedEffect
        if (height.value == 0f) height.snapTo(heightOf(detent)) else height.animateTo(heightOf(detent))
    }

    val dragState = rememberDraggableState { delta ->
        dragged = ((dragged ?: height.value) - delta).coerceIn(collapsedPx, maxPx)
    }
    val currentAnchors by rememberUpdatedState(listOf(collapsedPx, halfPx, maxPx))

    /** End of a drag of the sheet (header or content): rests on a detent, [velocity] in px/s, positive downwards. */
    suspend fun release(velocity: Float) {
        val from = dragged ?: return
        height.snapTo(from)
        dragged = null
        val anchors = currentAnchors
        val next = SheetDetent.entries[settle(from, velocity, anchors)]
        if (next != currentDetent) currentOnDetentChange(next)
        height.animateTo(anchors[next.ordinal])
    }

    val scope = rememberCoroutineScope()
    var pointerDown by remember { mutableStateOf(false) }
    // Content scrolled to its top and pulled further down: the sheet folds. Scrolled to its bottom and pushed
    // further up: the sheet grows. Once the sheet moves, it keeps the gesture until released.
    val contentScroll = remember {
        object : NestedScrollConnection {
            private var wheelSettle: Job? = null

            /** @return the part of [dy] (positive: finger down) the sheet took. */
            private fun moveSheet(dy: Float): Float {
                val from = dragged ?: height.value
                val anchors = currentAnchors
                val to = (from - dy).coerceIn(anchors.first(), anchors.last())
                if (to == from) return 0f
                dragged = to
                // A mouse wheel ends without a fling: one step in its direction once it stops.
                wheelSettle?.cancel()
                if (!pointerDown) {
                    wheelSettle = scope.launch {
                        delay(WHEEL_SETTLE_MILLIS)
                        release(if (dy > 0) FLING_VELOCITY + 1 else -FLING_VELOCITY - 1)
                    }
                }
                return from - to
            }

            override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset =
                if (dragged != null && source == NestedScrollSource.UserInput) Offset(0f, moveSheet(available.y)) else Offset.Zero

            override fun onPostScroll(consumed: Offset, available: Offset, source: NestedScrollSource): Offset =
                if (available.y != 0f && source == NestedScrollSource.UserInput) Offset(0f, moveSheet(available.y)) else Offset.Zero

            override suspend fun onPreFling(available: Velocity): Velocity {
                if (dragged == null) return Velocity.Zero
                wheelSettle?.cancel()
                release(available.y)
                return available
            }
        }
    }
    val expandLabel = stringResource(if (detent == SheetDetent.COLLAPSED) Res.string.expand else Res.string.collapse)

    Surface(
        modifier = modifier
            .fillMaxWidth()
            // Zero until the header is measured (it measures unbounded, whatever the sheet's height).
            .height(with(density) { (dragged ?: height.value).toDp() }),
        shape = RoundedCornerShape(topStart = 20.dp, topEnd = 20.dp),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 8.dp,
    ) {
        Column(Modifier.windowInsetsPadding(WindowInsets.navigationBars)) {
            Column(
                Modifier
                    .wrapContentHeight(Alignment.Top, unbounded = true)
                    .onSizeChanged { headerHeight = it.height }
                    .draggable(
                        state = dragState,
                        orientation = Orientation.Vertical,
                        onDragStopped = { velocity -> release(velocity) },
                    )
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                        currentOnDetentChange(if (currentDetent == SheetDetent.COLLAPSED) SheetDetent.HALF else SheetDetent.COLLAPSED)
                    }
                    .semantics { contentDescription = expandLabel },
            ) {
                Box(Modifier.fillMaxWidth().padding(top = 8.dp, bottom = 4.dp), contentAlignment = Alignment.Center) {
                    Box(
                        Modifier
                            .size(width = 36.dp, height = 4.dp)
                            .background(MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(2.dp)),
                    )
                }
                header()
            }
            Column(
                Modifier
                    // The whole sheet below the header takes the gesture, even under short content.
                    .weight(1f)
                    .pointerInput(Unit) {
                        awaitPointerEventScope {
                            while (true) {
                                pointerDown = awaitPointerEvent(PointerEventPass.Initial).changes.any { it.pressed }
                            }
                        }
                    }
                    // A wheel over content that cannot scroll never reaches the nested scroll: one detent per burst.
                    .pointerInput(Unit) {
                        var lastStep: TimeMark? = null
                        awaitPointerEventScope {
                            while (true) {
                                val event = awaitPointerEvent(PointerEventPass.Final)
                                val change = event.changes.firstOrNull() ?: continue
                                if (event.type != PointerEventType.Scroll || change.isConsumed || change.scrollDelta.y == 0f) continue
                                if (lastStep?.let { it.elapsedNow() < WHEEL_BURST } == true || dragged != null) continue
                                lastStep = TimeSource.Monotonic.markNow()
                                val step = if (change.scrollDelta.y > 0) 1 else -1
                                val next = SheetDetent.entries[(currentDetent.ordinal + step).coerceIn(0, SheetDetent.entries.lastIndex)]
                                if (next != currentDetent) currentOnDetentChange(next)
                            }
                        }
                    }
                    .nestedScroll(contentScroll)
                    .verticalScroll(rememberScrollState()),
            ) {
                content()
            }
        }
    }
}

/**
 * Index of the resting height to settle on after a drag ending at [from] px: a fling goes one step further
 * in its direction, a slow release goes to the nearest.
 */
internal fun settle(from: Float, velocity: Float, anchors: List<Float>): Int {
    val nearest = anchors.indices.minBy { abs(anchors[it] - from) }
    return when {
        // Finger moving up: the sheet grows.
        velocity < -FLING_VELOCITY -> anchors.indices.firstOrNull { anchors[it] > from + 1 } ?: anchors.lastIndex
        velocity > FLING_VELOCITY -> anchors.indices.lastOrNull { anchors[it] < from - 1 } ?: 0
        else -> nearest
    }
}
